import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, BookOpen, Star } from "lucide-react";
import { ORDER_LINKS } from "../data/menu";
import Sprinkles from "./Sprinkles";

export const Hero = () => {
  return (
    <section id="top" className="relative pt-24 pb-16 sm:pt-28 sm:pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
      {/* Soft gradient blob (pink, NOT purple/teal) */}
      <div aria-hidden className="absolute -top-32 -left-40 h-[520px] w-[520px] rounded-full bg-[#FFD6DD] blur-3xl opacity-70" />
      <div aria-hidden className="absolute top-20 right-[-160px] h-[420px] w-[420px] rounded-full bg-[#FFE7BE] blur-3xl opacity-60" />
      <Sprinkles count={24} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        {/* LEFT: editorial text block */}
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#FFB3C6] bg-white/70 backdrop-blur px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[#FF6392]"
            data-testid="hero-badge"
          >
            <Star className="h-3.5 w-3.5 fill-[#FF6392] text-[#FF6392]" />
            Best Donuts in the 956
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-6 font-serif-display text-4xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight text-[#2D2327]"
          >
            Hand‑rolled.
            <br />
            Pink‑glazed.{" "}
            <span className="relative inline-block">
              <span className="text-[#FF6392] italic">Iconic.</span>
              <svg className="absolute -bottom-3 left-0 w-full" height="14" viewBox="0 0 220 14" fill="none">
                <path d="M2 9 C 60 1, 160 1, 218 9" stroke="#FFD166" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-[#4A3F44] leading-relaxed"
          >
            Don Donuts is the Rio Grande Valley&apos;s favorite morning ritual — small‑batch donuts,
            crème‑brûlée crusts, Nutella‑drenched everything, and milkshakes worth the drive.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-wrap items-stretch sm:items-center gap-3"
          >
            <a
              href={ORDER_LINKS.orderOnline}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-order-online"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full bg-[#FF6392] text-white px-7 py-3.5 font-semibold hover:bg-[#E84A7A] hover:-translate-y-0.5 transition-all shadow-[0_10px_30px_rgba(255,99,146,0.35)]"
            >
              Order Online <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="#menu"
              data-testid="hero-browse-menu"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-2 rounded-full border-2 border-[#2D2327] text-[#2D2327] px-7 py-3.5 font-semibold hover:bg-[#2D2327] hover:text-white transition-all"
            >
              <BookOpen className="h-4 w-4" /> Browse Menu
            </a>
            <a
              href="#visit"
              data-testid="hero-visit-store"
              className="inline-flex w-full sm:w-auto justify-center sm:justify-start items-center gap-2 rounded-full text-[#2D2327] px-3 py-3.5 font-medium underline-offset-4 hover:underline"
            >
              <MapPin className="h-4 w-4 text-[#FF6392]" /> Visit Store
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mt-8 sm:mt-10 flex flex-col items-start sm:flex-row sm:items-center gap-3 sm:gap-6"
          >
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-[#FFD166] text-[#FFD166]" />
              ))}
              <span className="ml-2 text-sm text-[#4A3F44]">4.8 / 5 from 100+ reviews</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-sm text-[#4A3F44]">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              Open today · 7 AM – 7 PM
            </div>
          </motion.div>
        </div>

        {/* RIGHT: overlapping image collage */}
        <div className="lg:col-span-5 relative z-0 pointer-events-none h-[340px] sm:h-[560px] lg:h-[620px] mt-2 lg:mt-0">
          {/* big arch donut */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute top-0 right-0 sm:right-4 w-[78%] h-[78%] arch-mask overflow-hidden shadow-[0_30px_60px_rgba(255,99,146,0.25)] border border-white"
          >
            <img
              src="https://www.studiomediaagency.com/marketing/donuts-marketing7.webp"
              alt="The Don signature donut"
              className="h-full w-full object-cover"
              loading="eager"
            />
          </motion.div>

          {/* polaroid donut */}
          <motion.div
            initial={{ opacity: 0, rotate: -6, y: 30 }}
            animate={{ opacity: 1, rotate: -6, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute bottom-3 sm:bottom-6 left-0 w-[62%] sm:w-[58%] bg-white p-2 pb-4 sm:pb-6 shadow-[0_18px_40px_rgba(0,0,0,0.18)]"
          >
            <img
              src="https://www.studiomediaagency.com/marketing/donuts-marketing2.webp"
              alt="Box of donuts"
              className="w-full aspect-[4/5] object-cover"
              loading="lazy"
            />
            <div className="font-script text-xl sm:text-2xl text-[#2D2327] mt-2 text-center">freshly hand‑rolled.</div>
          </motion.div>

          {/* floating sticker */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5, type: "spring" }}
            className="hidden sm:grid absolute top-10 left-2 h-28 w-28 rounded-full bg-[#FFD166] text-[#2D2327] place-items-center spin-slow border-4 border-white shadow-[0_10px_24px_rgba(0,0,0,0.1)]"
            aria-hidden
          >
            <svg viewBox="0 0 120 120" className="absolute inset-0">
              <defs>
                <path id="circlePath" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
              </defs>
              <text fontSize="13" fontFamily="Playfair Display, serif" fill="#2D2327" letterSpacing="3">
                <textPath href="#circlePath">SINCE 956 · FRESH DAILY · MADE WITH LOVE · </textPath>
              </text>
            </svg>
            <div className="text-2xl font-serif-display">★</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
